const TOC_METHODS = new Set(['pdf_outline', 'metadata_api', 'ai_extraction', 'manual']);
const TOC_CONFIDENCE = new Set(['high', 'medium', 'low']);

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toTrimmedString = (value, fallback = '') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const normalizeSubchapter = (rawSubchapter) => {
  if (!rawSubchapter) return null;
  const title = toTrimmedString(rawSubchapter.title);
  if (!title) return null;
  return {
    title,
    pageStart: toFiniteNumber(rawSubchapter.pageStart),
  };
};

const normalizeChapter = (rawChapter, index) => {
  const title = toTrimmedString(rawChapter?.title);
  if (!title) return null;
  const subchapters = Array.isArray(rawChapter?.subchapters)
    ? rawChapter.subchapters.map(normalizeSubchapter).filter(Boolean)
    : [];
  return {
    chapterNumber: toFiniteNumber(rawChapter?.chapterNumber) ?? index + 1,
    title,
    pageStart: toFiniteNumber(rawChapter?.pageStart),
    pageEnd: toFiniteNumber(rawChapter?.pageEnd),
    subchapters,
    suggestedLessonCount: toFiniteNumber(rawChapter?.suggestedLessonCount),
  };
};

const normalizeLessonsToTotal = (baseAllocations, remainders, totalPeriods) => {
  const allocations = [...baseAllocations];
  let assigned = allocations.reduce((sum, value) => sum + value, 0);
  if (assigned < totalPeriods) {
    const orderedIndexes = [...remainders]
      .sort((a, b) => b.remainder - a.remainder)
      .map((item) => item.index);
    let cursor = 0;
    while (assigned < totalPeriods && orderedIndexes.length > 0) {
      const index = orderedIndexes[cursor % orderedIndexes.length];
      allocations[index] += 1;
      assigned += 1;
      cursor += 1;
    }
  } else if (assigned > totalPeriods) {
    const orderedIndexes = [...remainders]
      .sort((a, b) => a.remainder - b.remainder)
      .map((item) => item.index);
    let cursor = 0;
    while (assigned > totalPeriods && orderedIndexes.length > 0) {
      const index = orderedIndexes[cursor % orderedIndexes.length];
      if (allocations[index] > 0) {
        allocations[index] -= 1;
        assigned -= 1;
      }
      cursor += 1;
      if (cursor > orderedIndexes.length * 20) break;
    }
  }
  return allocations;
};

export function computeSuggestedLessonCounts(chapters, totalPeriods) {
  const safeChapters = Array.isArray(chapters) ? chapters : [];
  const periods = Math.max(1, Number(totalPeriods) || 1);
  if (safeChapters.length === 0) return [];

  const unitsByChapter = safeChapters.map((chapter) => {
    const subchapterCount = Array.isArray(chapter?.subchapters) ? chapter.subchapters.length : 0;
    return Math.max(1, subchapterCount + 1);
  });
  const totalUnits = unitsByChapter.reduce((sum, units) => sum + units, 0) || safeChapters.length;

  const raw = unitsByChapter.map((units, index) => {
    const exact = (periods * units) / totalUnits;
    const base = Math.max(1, Math.floor(exact));
    return { index, base, remainder: exact - base };
  });
  const baseAllocations = raw.map((item) => item.base);
  const finalAllocations = normalizeLessonsToTotal(baseAllocations, raw, periods);

  return safeChapters.map((chapter, index) => ({
    ...chapter,
    suggestedLessonCount: Math.max(1, Number(finalAllocations[index]) || 1),
  }));
}

export function normalizeSourceToc(rawSourceToc) {
  if (!rawSourceToc || typeof rawSourceToc !== 'object') return null;
  const extractionMethod = TOC_METHODS.has(rawSourceToc.extractionMethod)
    ? rawSourceToc.extractionMethod
    : null;
  const confidence = TOC_CONFIDENCE.has(rawSourceToc.confidence)
    ? rawSourceToc.confidence
    : null;
  const chapters = Array.isArray(rawSourceToc.chapters)
    ? rawSourceToc.chapters.map(normalizeChapter).filter(Boolean)
    : [];
  if (chapters.length === 0) return null;
  return {
    extractedAt: rawSourceToc.extractedAt || null,
    extractionMethod: extractionMethod || 'manual',
    confidence: confidence || 'low',
    chapters,
    totalChapters: chapters.length,
  };
}

export function buildBookStructurePromptBlock(sourceToc) {
  const normalized = normalizeSourceToc(sourceToc);
  if (!normalized) return '';
  const chapterLines = normalized.chapters.map((chapter, index) => {
    const chapterLabel = chapter.chapterNumber || index + 1;
    const lessonCount = Math.max(1, Number(chapter.suggestedLessonCount) || 1);
    return `Chapter ${chapterLabel}: "${chapter.title}" - ${lessonCount} lesson(s)`;
  });
  if (chapterLines.length === 0) return '';
  return [
    'BOOK STRUCTURE: This course is based on a book with the following chapter structure. Distribute lessons across chapters as indicated.',
    "Respect the book's chapter sequence - do not reorder chapters.",
    '',
    'Chapter distribution:',
    chapterLines.join('\n'),
    '',
    "Each chapter's lessons should cover that chapter's material in depth.",
    "Subchapters within a chapter should be distributed across that chapter's lesson slots.",
    "Do not introduce chapter N material in chapter M's lessons.",
  ].join('\n');
}

const lotsogSourceTocModule = {
  normalizeSourceToc,
  computeSuggestedLessonCounts,
  buildBookStructurePromptBlock,
};

export default lotsogSourceTocModule;
